import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import classNames from "classnames";
import * as c from "./dMFullBleedPageHeader.module.scss";
import { BREAKPOINTS } from "@utils/breakpoints";
import { processMarkdown } from "@utils/markdown";
import { ContentfulCta } from "@components/shared/cta";
import EmailCaptureForm from "@components/shared/emailCapture/emailCaptureForm";
import { useWindowSize } from "@hooks/useWindowSize";
import { useSlideEnterAnimation } from "@hooks/useSlideEnterAnimation";

export const DMFullBleedPageHeader = ({ pageHeaderData = {} }) => {
  const {
    label,
    labelColor,
    darkBackground,
    heading,
    subheading,
    cta,
    ctaSecondary,
    ctaEmailCapture,
    backgroundImage,
    mobileBackgroundImage,
    tabletBackgroundImage,
    backgroundColor,
    logoImage,
  } = pageHeaderData;

  const hasCtas = cta || ctaSecondary;
  const subheadingText = subheading?.subheading;
  const { width } = useWindowSize();
  const isMobile = width <= BREAKPOINTS.sm;
  const isTablet = width <= BREAKPOINTS.lg && width > BREAKPOINTS.sm;
  const isDesktop = width > BREAKPOINTS.lg;

  useSlideEnterAnimation();

  return (
    <div
      className={classNames(
        c.background,
        darkBackground && c.dark,
        backgroundColor?.value && backgroundImage && c.customBg
      )}
      style={{
        background: backgroundColor?.value ? backgroundColor?.value : "none",
        "--bg-color": backgroundColor?.value || "none",
        "--label-color": labelColor?.value || (darkBackground ? "#b7bce6" : "#fcfafa"),
      }}
    >
      {backgroundImage && isDesktop && (
        <GatsbyImage
          image={getImage(backgroundImage)}
          className={classNames(c.bg, c.backgroundImage)}
          loading="eager"
          alt={label ? `${label} - Background image` : "Background image"}
        />
      )}

      {tabletBackgroundImage && isTablet && (
        <GatsbyImage
          image={getImage(tabletBackgroundImage)}
          className={classNames(c.bg, c.backgroundImageTablet)}
          loading="eager"
          alt={label ? `${label} - Tablet Background image` : "Tablet Background image"}
        />
      )}

      {mobileBackgroundImage && isMobile && (
        <GatsbyImage
          image={getImage(mobileBackgroundImage)}
          className={classNames(c.bg, c.backgroundImageMobile)}
          loading="eager"
          alt={label ? `${label} - Mobile Background image` : "Mobile Background image"}
        />
      )}

      <div className={c.container}>
        <div className={c.landingPageRow}>
          <div className={c.directMailHeader} data-transition="slide-enter-child">
            {logoImage ? (
              <GatsbyImage image={getImage(logoImage)} alt="" className={c.labelImage} />
            ) : (
              label && <h2 className={classNames(c.eyebrow, "eyebrow")}>{label}</h2>
            )}

            <h1 className={classNames(c.headline, "h1")} dangerouslySetInnerHTML={{ __html: heading }} />

            {subheadingText && (
              <div
                className={classNames(c.subheading, "p2")}
                dangerouslySetInnerHTML={{ __html: processMarkdown(subheadingText) }}
              />
            )}

            {hasCtas && (
              <div className={classNames(c.ctaContainer, ctaSecondary && c.ctaWrap)}>
                {cta && (
                  <ContentfulCta
                    {...cta}
                    style={cta.style?.[0] !== "Button Secondary" ? "Button Primary" : "Button Secondary"}
                  />
                )}
                {ctaSecondary && <ContentfulCta {...ctaSecondary} />}
              </div>
            )}

            {ctaEmailCapture && (
              <EmailCaptureForm className={c.emailCapture} formSource="bm_newsletter" {...ctaEmailCapture} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
